import { forwardRef } from "react";
import { Search, X } from "lucide-react";
import { cn } from "@/lib/cn";
import type { InputSize } from "./input";

/**
 * Search input with leading icon and (optional) clear button.
 *
 * Operator pages use this 8+ times — extracted so the icon spacing,
 * input height, focus ring and clear-affordance stay in lock-step
 * across Issues / Inbox / Knowledge / Patches / Runs.
 *
 * The `data-data-table-search` attribute hooks the table's `/`
 * keyboard shortcut. Always set on toolbar searches that drive a
 * DataTable so operator muscle memory works on every page.
 */
export type SearchInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type" | "size"
> & {
  size?: InputSize;
  /** Optional clear handler — when present, an X button shows when value !== "". */
  onClear?: () => void;
  /** Sets the data attribute the DataTable keyboard layer focuses on `/`. */
  tableSearch?: boolean;
};

const SIZE_CLS: Record<InputSize, string> = {
  sm: "h-7 pl-7 pr-7 text-[12px]",
  md: "h-8 pl-8 pr-8 text-[12.5px]",
};
const ICON_OFFSET: Record<InputSize, string> = { sm: "left-2", md: "left-2.5" };
const CLEAR_OFFSET: Record<InputSize, string> = { sm: "right-1.5", md: "right-2" };

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  function SearchInput(
    { size = "md", onClear, tableSearch, className, value, placeholder = "Search…", ...rest },
    ref,
  ) {
    return (
      <label className={cn("relative inline-flex items-center", className)}>
        <Search
          size={12}
          aria-hidden
          className={cn("absolute text-fg-muted pointer-events-none", ICON_OFFSET[size])}
        />
        <input
          ref={ref}
          type="search"
          value={value}
          placeholder={placeholder}
          data-data-table-search={tableSearch ? "" : undefined}
          className={cn(
            "w-full rounded-md bg-bg-base border border-line-subtle text-fg placeholder:text-fg-muted",
            "focus-ring focus-visible:border-brand-500/50",
            // Hide the WebKit reset ✕; we render our own when onClear is given.
            "[&::-webkit-search-cancel-button]:appearance-none",
            SIZE_CLS[size],
          )}
          {...rest}
        />
        {onClear && typeof value === "string" && value.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            tabIndex={-1}
            aria-label="Clear search"
            className={cn(
              "absolute h-5 w-5 grid place-items-center rounded-sm text-fg-muted hover:text-fg hover:bg-bg-hover",
              CLEAR_OFFSET[size],
            )}
          >
            <X size={11} />
          </button>
        )}
      </label>
    );
  },
);
